import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const PALETTE = ["#3B82F6", "#F59E0B", "#10B981", "#EC4899", "#8B5CF6", "#06B6D4", "#EF4444", "#84CC16",
  "#F97316", "#14B8A6", "#A855F7", "#EAB308", "#22D3EE", "#F43F5E", "#6366F1", "#65A30D"];
export const STALE_MIN = 10;

// Stable per-driver colour (same id -> same colour across sessions).
export const driverColor = (id) => {
  let h = 0;
  for (const ch of String(id)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

export const isStale = (ts) => !ts || Date.now() - new Date(ts).getTime() > STALE_MIN * 60000;

function iconHtml(d) {
  const c = driverColor(d.id);
  const stale = isStale(d.last_ping);
  const pulse = !stale ? `<span style="position:absolute;inset:0;border-radius:9999px;background:${c};animation:rs-ping 1.8s cubic-bezier(0,0,0.2,1) infinite"></span>` : "";
  return `<span style="position:relative;display:flex;height:16px;width:16px;align-items:center;justify-content:center;opacity:${stale ? 0.55 : 1}">${pulse}<span style="position:relative;height:13px;width:13px;border-radius:9999px;background:${stale ? "transparent" : c};border:3px solid ${c};box-shadow:0 0 0 1px #0a0f1a"></span></span>`;
}

// Vanilla Leaflet wrapper (StrictMode-safe; avoids react-leaflet effect-cleanup issues on React 19).
// Only drivers with a real reported position (last_ping) are drawn.
export default function LiveMap({ drivers = [], onSelect, heightClass = "h-[360px]", scrollZoom = false }) {
  const elRef = useRef(null);
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;
  const mapRef = useRef(null);
  const groupRef = useRef(null);
  const fitted = useRef(false);

  useEffect(() => {
    if (!elRef.current || mapRef.current) return undefined;
    const map = L.map(elRef.current, { zoomControl: true, scrollWheelZoom: scrollZoom, attributionControl: true });
    map.setView([42.36, -71.06], 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      subdomains: "abc", className: "rs-dark-tiles", maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    groupRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 120);
    const onResize = () => mapRef.current && mapRef.current.invalidateSize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); map.remove(); mapRef.current = null; groupRef.current = null; fitted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current, group = groupRef.current;
    if (!map || !group) return;
    group.clearLayers();
    const valid = drivers.filter((d) => typeof d.lat === "number" && typeof d.lng === "number" && d.last_ping);
    valid.forEach((d) => {
      const m = L.marker([d.lat, d.lng], { icon: L.divIcon({ className: "", html: iconHtml(d), iconSize: [16, 16], iconAnchor: [8, 8] }) });
      m.bindTooltip(`${d.name}${isStale(d.last_ping) ? " (stale)" : ""}`, { permanent: true, direction: "right", offset: [8, 0], className: "rs-driver-label" });
      m.on("click", () => selectRef.current && selectRef.current(d.id));
      m.addTo(group);
    });
    if (!fitted.current && valid.length) {
      const pts = valid.map((d) => [d.lat, d.lng]);
      if (pts.length === 1) map.setView(pts[0], 13); else map.fitBounds(pts, { padding: [40, 40] });
      fitted.current = true;
    }
  }, [drivers]);

  return <div ref={elRef} className={`relative z-0 ${heightClass} w-full overflow-hidden rounded-lg border border-slate-800`} data-testid="live-map" />;
}
